/**
 * Field's Home as a dashboard (Lj, Oct 11: "same quality bar as the Dev Home" — the All Home's polish and chart
 * language, ALL of Field's data, every card charted). One shape for the phone and the web. Every number comes from the
 * Field goals' own counts (fieldgoals.js) or the engine (deptengine.js), so a card and the score never disagree; each
 * block carries `counted` (what it counted) and `leftOut` (what it didn't). The running timer counts when the caller
 * passed it in (withLiveTime). Pure — no I/O.
 */
import { goalWindow, goalsFor, resolveTarget, deptRole, jobBurn, goalValue, isMaterialRun } from './deptgoals.js';
import {
  FIELD_PRESETS, fieldEarnedPerHour, fieldEfficiency, fieldProductiveShare, fieldJobMargin, fieldJobCost, FIELD_WORK_KIND, payRateOf,
} from './fieldgoals.js';
import { fieldEngine, cleanHomeProjectIds } from './deptengine.js';
import { workDepartments } from './workdept.js';
import { departmentOf } from './hierarchy.js';
import { contractValueOf } from './profit.js';
import { photosOfTask, mediaByTask } from './photoindex.js';
import { addDaysStr } from './phonestats.js';
import { TRIP_DEFAULTS } from './trips.js';
import { isFuelLine } from './metrics.js';
import { effectiveScope, planStartFor } from './scope.js';
import { aheadDaysOf } from './projectdash.js';
import { localDateStr } from './time.js';

const num = (v) => (v == null || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));
const open = (t) => t && t.status !== 'done' && t.status !== 'skipped' && !t.deletedAt;
const span = (from, to) => ({ period: 'week', from, to, end: to, share: 1, off: false });
const LIVE = (p) => p && !p.deletedAt && !p.archived && !['archived', 'completed', 'cancelled'].includes(p.status);

/** The productive bar's parts, in order: Field's own work three ways (+ other quick tasks), then everything else worked. */
export const FIELD_TIME_KINDS = Object.freeze(['Job tasks', 'Setup + cleanup', 'Runs + travel', 'Other quick tasks', 'Other work']);

/**
 * Field's targets for the cards: each goal's own target when the department has that goal (a rate link or the jobs'
 * blend resolved), else the preset's start (Earned per hour: the jobs' blend, v0.1.258).
 * → { [key]: { target, from: 'goal' | 'rate' | 'jobs' | 'preset', direction } }
 */
export function fieldTargets(dept, data = {}) {
  const goals = goalsFor(dept);
  const rate = payRateOf(data);
  const out = {};
  for (const p of FIELD_PRESETS) {
    const g = goals.find((x) => x.key === p.key);
    const from = (g ? g.targetFrom : p.targetFrom)?.kind || null;
    const got = from === 'jobs' ? fieldEarnedPerHour(dept, goalWindow(g?.period || p.period || 'week', data.now || Date.now()), data) : null;
    const t = g ? resolveTarget(g, data, got) : null;
    const linked = from === 'payRate';
    const pct = (g ? g.targetFrom?.pct : p.targetFrom?.pct) ?? 100;
    const jobs = from === 'jobs' && got?.jobTarget?.value != null;
    if (g && t != null) out[p.key] = { target: t, from: linked ? (rate != null ? 'rate' : 'preset') : jobs ? 'jobs' : from === 'jobs' ? 'preset' : 'goal', pct: linked ? pct : null, direction: g.direction || p.direction || 'up', jobs: jobs ? got.jobTarget.jobs : null };
    else if (jobs) out[p.key] = { target: got.jobTarget.value, from: 'jobs', direction: p.direction || 'up', jobs: got.jobTarget.jobs };
    else if (linked && rate != null) out[p.key] = { target: Math.round(rate * (pct / 100) * 100) / 100, from: 'rate', pct, direction: p.direction || 'up' };
    else out[p.key] = { target: p.target, from: 'preset', direction: p.direction || 'up' };
  }
  return out;
}

/** The projects pinned to Field's Home before any pin was ever set: its live jobs, the most recently worked first (≤ 6). */
export function defaultFieldPins(dept, data = {}) {
  const { tasks = [], projects = [], departments = [] } = data;
  const last = new Map();
  for (const t of tasks) for (const e of t.timeEntries || []) if (t.projectId && e?.date && e.date > (last.get(t.projectId) || '')) last.set(t.projectId, e.date);
  return cleanHomeProjectIds(projects.filter((p) => LIVE(p) && departmentOf(p, departments).id === dept?.id)
    .sort((a, b) => String(last.get(b.id) || '').localeCompare(String(last.get(a.id) || '')) || (num(b.updatedAt) || 0) - (num(a.updatedAt) || 0))
    .slice(0, 6).map((p) => p.id));
}

export const FIELD_RANGES = Object.freeze(['day', 'week', 'month', 'custom']);
const MAX_RANGE_DAYS = 92;
const shortDate = (date) => new Date(`${date}T12:00:00`).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
const dayCount = (from, to) => Math.round((new Date(`${to}T12:00:00`) - new Date(`${from}T12:00:00`)) / 86400000) + 1;

/**
 * The window every Field card reads (Lj: "idk where the date range pickers went"). `custom` = { from, to } (local days);
 * a bad or missing custom falls back to the week. → { range, from, to, end, label, prev: { from, to } } where `prev` is
 * the same stretch just before: yesterday, last week, last month, the days before a custom range.
 */
export function fieldWindow(range = 'week', now = Date.now(), custom = null) {
  const today = localDateStr(now);
  if (range === 'custom' && custom?.from && custom?.to && custom.from <= custom.to) {
    const to = custom.to; const from = dayCount(custom.from, to) > MAX_RANGE_DAYS ? addDaysStr(to, 1 - MAX_RANGE_DAYS) : custom.from;
    const n = dayCount(from, to);
    return { range, from, to, end: to, label: from === to ? shortDate(from) : `${shortDate(from)} – ${shortDate(to)}`, prev: { from: addDaysStr(from, -n), to: addDaysStr(from, -1) } };
  }
  if (range === 'day') return { range, from: today, to: today, end: today, label: 'today', prev: { from: addDaysStr(today, -1), to: addDaysStr(today, -1) } };
  if (range === 'month') {
    const w = goalWindow('month', now); const p = goalWindow('month', now, 1);
    return { range, from: w.from, to: w.to, end: w.end ?? w.to, label: 'this month', prev: { from: p.from, to: p.to } };
  }
  const w = goalWindow('week', now);
  return { range: 'week', from: w.from, to: w.to, end: w.end ?? w.to, label: 'this week', prev: { from: addDaysStr(w.from, -7), to: addDaysStr(w.from, -1) } };
}

const daysBetween = (from, to) => Array.from({ length: Math.max(1, dayCount(from, to)) }, (_, i) => addDaysStr(from, i));

/**
 * The time and cost around the job: travel, breaks, fuel, pickups, each this window vs the one before, plus by day; where
 * the hours went by scope; hours of quoted work left. `chartDays` = the days the tiles chart. Drives come from the clock
 * sessions (never one left open from an earlier day), breaks from each Field task's status log, fuel from receipts when
 * there are any, else km at the vehicle's L/100 km × $/L (`estimate: true`).
 */
export function fieldTimeCosts(dept, data = {}, win, chartDays = []) {
  const now = data.now || Date.now();
  const today = localDateStr(now);
  const tasks = data.tasks || [];
  const byId = new Map(tasks.map((t) => [t.id, t]));
  const projects = data.projects || [];
  const wd = workDepartments({ tasks, projects, departments: data.departments || [], departmentMode: data.departmentMode ?? true });
  const inW = (date, w) => !!date && date >= w.from && date <= w.to;
  const vehicle = { ...TRIP_DEFAULTS, ...(data.vehicle || {}) };
  const fuelOf = (km) => Math.round(km * (vehicle.lPer100km / 100) * vehicle.fuelPrice * 100) / 100;

  const drives = (data.sessions || []).filter((s) => s?.type === 'travel' && s.startedAt && (s.endedAt || localDateStr(s.startedAt) === today))
    .map((s) => ({ date: localDateStr(s.startedAt), ms: s.endedAt ? (num(s.duration) || s.endedAt - s.startedAt) : Math.max(0, now - s.startedAt), km: (num(s.distanceM) || 0) / 1000 }));
  const sum = (list, k, w) => list.filter((x) => inW(x.date, w)).reduce((a, x) => a + (x[k] || 0), 0);
  const byDay = (list, k) => chartDays.map((date) => ({ date, value: list.filter((x) => x.date === date).reduce((a, x) => a + (x[k] || 0), 0) }));

  const pauses = [];
  for (const t of tasks) {
    if (t.deletedAt || wd.forTask(t)?.id !== dept?.id) continue;
    for (const l of t.statusLog || []) if (l?.startedAt && (l.duration || 0) > 0) pauses.push({ date: localDateStr(l.startedAt), ms: l.duration });
  }

  const fuelBought = [];
  for (const r of receiptRows(data.receipts)) for (const l of r.lines) if (isFuelLine(l) && num(l.amount) > 0) fuelBought.push({ date: r.date, amount: num(l.amount) });

  const pickups = [];
  for (const t of tasks) {
    if (t.deletedAt || !isMaterialRun(t)) continue;
    for (const e of t.timeEntries || []) if (e?.date && (e.duration || 0) > 0) pickups.push({ date: e.date, ms: e.duration, taskId: t.id });
  }

  const km = sum(drives, 'km', win); const kmPrev = sum(drives, 'km', win.prev);
  const bought = sum(fuelBought, 'amount', win); const boughtPrev = sum(fuelBought, 'amount', win.prev);

  // where the hours went: this window's Field entries by each task's scope
  const planAt = new Map();
  const planOf = (pid) => { if (!planAt.has(pid)) planAt.set(pid, planStartFor(projects.find((p) => p.id === pid) || null, data.sessions || [], tasks)); return planAt.get(pid); };
  const scope = { included: 0, extra: 0, missed: 0, overhead: 0, unsorted: 0, none: 0 };
  for (const t of tasks) {
    if (t.deletedAt) continue;
    const pid = t.projectId || (t.parentId ? byId.get(t.parentId)?.projectId : null) || null;
    let ms = 0;
    for (const e of t.timeEntries || []) if (inW(e?.date, win) && (e.duration || 0) > 0 && wd.forEntry(e, t)?.id === dept?.id) ms += e.duration;
    if (!ms) continue;
    const at = pid ? planOf(pid) : null;
    const s = effectiveScope(t, { planAt: at, byId });
    scope[s || (Number.isFinite(at) && !t.bucket ? 'unsorted' : 'none')] += ms;
  }

  // quoted hours left on live jobs here
  const left = [];
  for (const p of projects) {
    if (!LIVE(p) || !(num(p.value) > 0) || departmentOf(p, data.departments || []).id !== dept?.id) continue;
    const burn = jobBurn(tasks, p.id);
    if (burn?.estMin > 0) left.push({ project: p, ms: Math.round(burn.estMin * (1 - burn.doneShare) * 60000) });
  }

  return {
    travel: { ms: sum(drives, 'ms', win), km: Math.round(km * 10) / 10, n: drives.filter((x) => inW(x.date, win)).length, prevMs: sum(drives, 'ms', win.prev), days: byDay(drives, 'ms') },
    breaks: { ms: sum(pauses, 'ms', win), n: pauses.filter((x) => inW(x.date, win)).length, prevMs: sum(pauses, 'ms', win.prev), days: byDay(pauses, 'ms') },
    fuel: { cost: bought > 0 ? Math.round(bought * 100) / 100 : fuelOf(km), estimate: !(bought > 0), prev: boughtPrev > 0 ? Math.round(boughtPrev * 100) / 100 : fuelOf(kmPrev), days: byDay(drives, 'km').map((x) => ({ date: x.date, value: fuelOf(x.value) })), vehicleSet: !!data.vehicle },
    pickups: { ms: sum(pickups, 'ms', win), n: new Set(pickups.filter((x) => inW(x.date, win)).map((x) => x.taskId)).size, prevMs: sum(pickups, 'ms', win.prev), days: byDay(pickups, 'ms') },
    scope,
    workLeft: { ms: left.reduce((a, x) => a + x.ms, 0), jobs: left.filter((x) => x.ms > 0).sort((a, b) => b.ms - a.ms) },
  };
}

/**
 * Field's Home job list: live jobs worked in the last 14 days, last worked first, then the pins not touched lately. The
 * pin list was seeded once and froze (three finished jobs on Home, Ken and Derricks Roof missing). ≤ `limit`.
 */
export function fieldHomeJobIds(dept, data = {}, pins = [], { limit = 8 } = {}) {
  const { tasks = [], projects = [], departments = [] } = data;
  const from = addDaysStr(localDateStr(data.now || Date.now()), -13);
  const last = new Map();
  for (const t of tasks) for (const e of t.timeEntries || []) if (t.projectId && e?.date && e.date >= from && e.date > (last.get(t.projectId) || '')) last.set(t.projectId, e.date);
  const live = new Map(projects.filter((p) => LIVE(p)).map((p) => [p.id, p]));
  const recent = [...live.values()].filter((p) => departmentOf(p, departments).id === dept?.id && last.has(p.id))
    .sort((a, b) => last.get(b.id).localeCompare(last.get(a.id))).map((p) => p.id);
  return cleanHomeProjectIds([...recent, ...(pins || []).filter((id) => live.has(id) && !recent.includes(id))]).slice(0, limit);
}

/** Receipts that aren't void / removed, each with the day it was snapped (its page date, else when the books sent it), its total, and its lines' jobs. */
function receiptRows(receipts = []) {
  const out = [];
  for (const r of receipts || []) {
    if (!r || r.status === 'void' || r.status === 'removed') continue;
    const pages = Object.values(r.pages || {}).filter((p) => p && p.status !== 'void');
    const dates = pages.map((p) => num(p.date)).filter((v) => v > 0);
    const at = dates.length ? Math.min(...dates) : num(r.updatedAt) || num(r.createdAt) || null;
    const lines = pages.flatMap((p) => p.lines || []);
    const loose = pages.some((p) => !(p.lines || []).length || (p.lines || []).some((l) => !l.projectId));
    out.push({ id: r.id || r.captureId, date: at ? localDateStr(at) : null, total: num(r.total) || 0, loose, vendor: pages.find((p) => p.vendorName)?.vendorName || 'A receipt', jobs: new Set(lines.map((l) => l.projectId).filter(Boolean)), lines });
  }
  return out;
}

/**
 * One job's card on Field's Home (pinned with the same `homeProjectIds` as Dev's apps): contract value, spent, margin
 * (the job cost screen's own calculation — `data.jobCost(project)` from the client, else worked out here), hours this
 * week (live), % of the estimate done, the next task, the last photo, receipts on the job.
 * No books job and no expenses logged here = `notLinked`: there's nothing to total, which is not the same as $0.
 * Your own unpaid time adds nothing to `spent`; `yourTime` is it at your shadow rate, on its own line, never mixed in.
 * No margin until a cost exists (100% on a job with no costs posted says nothing).
 * → { project, contract, spent, notLinked, yourTime, revenue, marginPct, weekMs, days, donePct, doneN, totalN, next, photo, receipts, running }
 */
export function fieldJobCard(project, data = {}, { activeTaskId = null } = {}) {
  const now = data.now || Date.now();
  const today = localDateStr(now);
  const wk = goalWindow('week', now);
  const tasks = data.tasks || [];
  const byId = new Map(tasks.map((t) => [t.id, t]));
  const pidOf = (t) => t?.projectId || (t?.parentId ? byId.get(t.parentId)?.projectId : null) || null;
  const mine = tasks.filter((t) => !t.deletedAt && pidOf(t) === project.id);
  const from7 = addDaysStr(today, -6);
  let weekMs = 0; const perDay = new Map();
  for (const t of mine) for (const e of t.timeEntries || []) {
    if (!e?.date || !((e.duration || 0) > 0)) continue;
    if (e.date >= wk.from && e.date <= wk.to) weekMs += e.duration;
    if (e.date >= from7 && e.date <= today) perDay.set(e.date, (perDay.get(e.date) || 0) + e.duration);
  }
  const job = project.bookProjectId ? (data.jobs || []).find((j) => j.id === project.bookProjectId) || null : null;
  const contract = contractValueOf(project, job) || 0;
  let cfo = null;
  try { cfo = (typeof data.jobCost === 'function' ? data.jobCost(project) : fieldJobCost(project, data))?.cfo || null; } catch { cfo = null; }
  const spent = cfo ? Math.round((num(cfo.jobCosts) || 0) + (num(cfo.labour?.actual) || 0) + (num(cfo.overhead?.actual) || 0)) : null;
  // ahead or behind: a job with an end date, quoted minutes finished vs quoted minutes dated up to today, at 8 h a day
  let aheadDays = null;
  if (project.endDate) {
    aheadDays = aheadDaysOf(mine.filter((t) => !t.parentId && !t.bucket && num(t.estimatedMinutes) > 0 && t.status !== 'skipped'), today);
  }
  const notLinked = !job && !(num(cfo?.jobCosts) > 0);
  const costed = (num(cfo?.jobCosts) || 0) + (num(cfo?.labour?.actual) || 0) > 0;
  const paidOut = num(cfo?.labour?.paidOut);
  const top = mine.filter((t) => !t.parentId && !t.bucket && !t.quickTaskId && t.status !== 'skipped');
  const burn = jobBurn(tasks, project.id);
  const order = (t) => num(t.sortOrder) ?? num(t.manualOrder) ?? num(t.createdAt) ?? 0;
  const openTop = top.filter(open);
  const dated = openTop.filter((t) => t.scheduledDate).sort((a, b) => String(a.scheduledDate).localeCompare(String(b.scheduledDate)) || order(a) - order(b));
  const nx = dated[0] || [...openTop].sort((a, b) => order(a) - order(b))[0] || null;
  const mediaIdx = data.mediaByTask instanceof Map ? data.mediaByTask : mediaByTask(data.media);
  const photos = mine.flatMap((t) => photosOfTask(t, mediaIdx).map((p) => ({ ...p, taskId: t.id }))).sort((a, b) => b.at - a.at);
  const rec = receiptRows(data.receipts).filter((r) => r.jobs.has(project.id));
  const recTotal = rec.reduce((a, r) => a + r.lines.filter((l) => l.projectId === project.id).reduce((x, l) => x + (num(l.amount) || 0), 0), 0);
  const running = activeTaskId && byId.get(activeTaskId) && pidOf(byId.get(activeTaskId)) === project.id ? byId.get(activeTaskId) : null;
  return {
    project, contract, rate: num(project.hourlyRate) || null, revenue: cfo ? num(cfo.revenue?.amount) : null, aheadDays, spent: notLinked ? null : spent, notLinked, yourTime: paidOut > 0 ? Math.round(paidOut) : null, marginPct: costed && cfo?.marginPct ? cfo.marginPct.actual : null, paidPct: costed && cfo?.marginPct ? cfo.marginPct.paidOut : null,
    weekMs, days: Array.from({ length: 7 }, (_, i) => { const date = addDaysStr(from7, i); return { date, ms: perDay.get(date) || 0 }; }),
    donePct: burn?.estMin ? Math.round(burn.doneShare * 100) : top.length ? Math.round((top.filter((t) => t.status === 'done').length / top.length) * 100) : null,
    doneBy: burn?.estMin ? 'estimate' : 'tasks', doneN: top.filter((t) => t.status === 'done').length, totalN: top.length,
    next: nx ? { task: nx, date: nx.scheduledDate || null, overdue: !!nx.scheduledDate && nx.scheduledDate < today } : null,
    photo: photos[0] || null, photos: photos.length,
    receipts: { n: rec.length, total: Math.round(recTotal * 100) / 100 },
    running, address: String(project.address || '').trim() || null,
  };
}

/**
 * Field's whole dashboard. `opts` = { clock, activeTaskId } (what's running). Everything over the last 14 days / 8
 * weeks from the device. → { engine, targets, hours, kpi, earned, efficiency, productive, photos, receipts, runs,
 *   callbacks, margins }
 */
export function fieldDashboard(dept, data = {}, { clock = null, activeTaskId = null, range = 'week', custom = null } = {}) {
  const now = data.now || Date.now();
  const win = fieldWindow(range, now, custom);
  const isMonth = win.range === 'month';
  const d = { ...data, now };
  const today = localDateStr(now);
  const tasks = data.tasks || [];
  const byId = new Map(tasks.map((t) => [t.id, t]));
  const projById = new Map((data.projects || []).map((p) => [p.id, p]));
  const wd = workDepartments({ tasks, projects: data.projects || [], departments: data.departments || [], departmentMode: data.departmentMode ?? true });
  const mineTask = (t) => wd.forTask(t)?.id === dept?.id;
  const pidOf = (t) => t?.projectId || (t?.parentId ? byId.get(t.parentId)?.projectId : null) || null;
  // One range for every card (Lj 2026-10-26): day or week = the last 14 days on the charts; month = the last 30; custom =
  // its own days, at least 14 ending at its last day.
  const chartEnd = win.range === 'custom' && win.to < today ? win.to : today;
  const nDays = isMonth ? 30 : win.range === 'custom' ? Math.max(14, dayCount(win.from, win.to)) : 14;
  const days = Array.from({ length: nDays }, (_, i) => addDaysStr(chartEnd, i - (nDays - 1)));
  const wk = { ...span(win.from, win.to), period: win.range === 'month' ? 'month' : win.range === 'week' ? 'week' : 'range' };
  const winDays = daysBetween(win.from, win.to);
  const thisWeek = goalWindow('week', now);
  const weekStarts = Array.from({ length: 8 }, (_, i) => addDaysStr(thisWeek.from, (i - 7) * 7));
  const wkSpan = (w) => span(w, w === thisWeek.from ? today : addDaysStr(w, 6));
  const targets = fieldTargets(dept, d);
  const name = String(dept?.name || 'Field');

  // ── hours: every entry run under Field, by job, by kind ───────────────────────────────────────────────────────
  const perDayJob = new Map(days.map((x) => [x, new Map()]));
  const perWeek = new Map(weekStarts.map((w) => [w, 0]));
  const tot14 = new Map();
  const kindToday = new Map(); const kindWeek = new Map();
  let liveMs = 0;
  for (const t of tasks) {
    for (const e of t.timeEntries || []) {
      if (!e?.date || !((e.duration || 0) > 0)) continue;
      if (wd.forEntry(e, t)?.id !== dept?.id) {
        // other departments' hours — the productive bar's "Other work" (Lj: an invoice is overhead, field work isn't)
        if (e.date === today) kindToday.set('Other work', (kindToday.get('Other work') || 0) + e.duration);
        if (e.date >= wk.from && e.date <= wk.to) kindWeek.set('Other work', (kindWeek.get('Other work') || 0) + e.duration);
        continue;
      }
      if (e.live) liveMs += e.duration;
      const ws = weekStarts.find((w) => e.date >= w && e.date <= addDaysStr(w, 6));
      if (ws) perWeek.set(ws, perWeek.get(ws) + e.duration);
      const kind = FIELD_WORK_KIND(t);
      if (e.date === today) kindToday.set(kind, (kindToday.get(kind) || 0) + e.duration);
      if (e.date >= wk.from && e.date <= wk.to) kindWeek.set(kind, (kindWeek.get(kind) || 0) + e.duration);
      const m = perDayJob.get(e.date);
      if (!m) continue;
      const pid = pidOf(t) || '';
      m.set(pid, (m.get(pid) || 0) + e.duration);
      tot14.set(pid, (tot14.get(pid) || 0) + e.duration);
    }
  }
  const ranked = [...tot14].filter(([pid]) => pid).sort((a, b) => b[1] - a[1]);
  const series = ranked.slice(0, 5).map(([pid, ms]) => ({ id: pid, name: projById.get(pid)?.name || 'A job', color: projById.get(pid)?.color || null, ms }));
  const keep = new Set(series.map((x) => x.id));
  const otherMs = [...tot14].filter(([pid]) => !keep.has(pid)).reduce((a, [, v]) => a + v, 0);
  if (otherMs > 0) series.push({ id: 'other', name: ranked.length > 5 ? 'Other / no job' : 'No job', color: null, ms: otherMs });
  const perDay = days.map((date) => {
    const m = perDayJob.get(date);
    const parts = series.map((x) => ({ id: x.id, ms: x.id === 'other' ? [...m].filter(([pid]) => !keep.has(pid)).reduce((a, [, v]) => a + v, 0) : m.get(x.id) || 0 }));
    return { date, parts, ms: parts.reduce((a, p) => a + p.ms, 0) };
  });
  const worked = perDay.filter((x) => x.date !== today && x.ms > 0);
  const hoursGoal = targets.hoursWorked.target;
  const kinds = (m) => FIELD_TIME_KINDS.map((k) => ({ id: k, ms: m.get(k) || 0 })).filter((x) => x.ms > 0);
  const share = (m) => { const all = [...m.values()].reduce((a, v) => a + v, 0); return all ? Math.round(((all - (m.get('Other work') || 0)) / all) * 100) : null; };
  const hours = {
    today: perDay.find((x) => x.date === today)?.ms || 0, avg: worked.length ? worked.reduce((a, x) => a + x.ms, 0) / worked.length : 0, perDay, series, liveMs,
    week: perWeek.get(thisWeek.from) || 0, inRange: perDay.filter((x) => x.date >= wk.from && x.date <= wk.to).reduce((a, x) => a + x.ms, 0), lastWeek: perWeek.get(weekStarts[6]) || 0, weeks: weekStarts.map((w) => ({ from: w, ms: perWeek.get(w) || 0 })),
    dayTarget: hoursGoal ? Math.round((hoursGoal / 5) * 10) / 10 : null, goalFrom: targets.hoursWorked.from,
    counted: `${name} entries only — the job each task belongs to${liveMs ? '; the running timer counted live' : ''}`,
    leftOut: ['clock time with no task running', 'other departments’ work on the same day'],
  };

  // ── earned per hour: this week (the goal's count), each day, 8 weeks, per job ────────────────────────────────
  const earnedWeek = fieldEarnedPerHour(dept, wk, d);
  const earnedDays = days.map((date) => ({ date, value: fieldEarnedPerHour(dept, span(date, date), d).value }));
  const earnedWeeks = weekStarts.map((w) => ({ from: w, value: fieldEarnedPerHour(dept, wkSpan(w), d).value }));
  const earned = {
    value: earnedWeek.value, target: targets.earnedPerHour.target, targetFrom: targets.earnedPerHour.from, targetPct: targets.earnedPerHour.pct ?? null,
    days: earnedDays, weeks: earnedWeeks, earned: Math.round(earnedWeek.earned || 0), ms: earnedWeek.ms || 0,
    jobs: (earnedWeek.jobs || []).map((j) => ({ ...j, project: projById.get(j.projectId) || { id: j.projectId, name: 'A job' } })),
    counted: earnedWeek.basis, leftOut: earnedWeek.notes || [],
  };

  // ── efficiency: estimate ÷ actual on the tasks finished this week ───────────────────────────────────────────
  const effWeek = fieldEfficiency(dept, wk, d);
  const effTasks = (effWeek.rows || []).map((r) => {
    const t = byId.get(r.taskId);
    if (!t) return null;
    const estMs = (t.estimatedMinutes || 0) * 60000; const actualMs = t.timeSpent || 0;
    return { task: t, estMs, actualMs, pct: actualMs ? Math.round((estMs / actualMs) * 100) : null, date: r.date, project: projById.get(pidOf(t)) || null };
  }).filter(Boolean).sort((a, b) => (a.pct ?? 999) - (b.pct ?? 999));
  const efficiency = {
    value: effWeek.value, target: targets.onEstimate.target, tasks: effTasks, worst: effTasks.filter((x) => x.pct != null && x.pct < 100).slice(0, 3),
    weeks: weekStarts.map((w) => ({ from: w, value: fieldEfficiency(dept, wkSpan(w), d).value })),
    counted: effWeek.basis, leftOut: effWeek.notes || [],
  };

  // ── productive vs overhead: today and this week by kind; the share each day ─────────────────────────────────
  const prodWeek = fieldProductiveShare(dept, wk, d);
  const productive = {
    value: prodWeek.value, target: targets.productiveShare.target, today: { share: share(kindToday), parts: kinds(kindToday) }, week: { share: share(kindWeek), parts: kinds(kindWeek) },
    days: days.map((date) => ({ date, value: fieldProductiveShare(dept, span(date, date), d).value })),
    counted: prodWeek.basis, leftOut: prodWeek.notes || [],
  };

  // ── photos this week, by the day taken ─────────────────────────────────────────────────────────────────────
  const shots = [];
  const mediaIdx = mediaByTask(data.media);
  for (const t of tasks) {
    if (t.deletedAt || !mineTask(t)) continue;
    for (const p of photosOfTask(t, mediaIdx)) {
      if (!(p.at > 0)) continue;
      const date = localDateStr(p.at);
      if (date >= wk.from && date <= wk.to) shots.push({ ...p, date, taskId: t.id, taskTitle: t.title, projectId: pidOf(t) });
    }
  }
  shots.sort((a, b) => b.at - a.at);
  const photoCount = (tag) => shots.filter((p) => p.tag === tag).length;
  const photos = {
    n: shots.length, list: shots, counts: { before: photoCount('before'), progress: photoCount('progress'), after: photoCount('after') }, target: targets.photosTaken.target,
    days: winDays.map((date) => ({ date, n: shots.filter((p) => p.date === date).length, future: date > today })),
    counted: `${shots.length} photo${shots.length === 1 ? '' : 's'} on ${name} tasks, by the day taken`, leftOut: shots.some((p) => !p.takenAt) ? ['photos with no taken time are counted on the day added'] : [],
  };

  // ── receipts snapped this week, and the ones on no job ─────────────────────────────────────────────────────
  const recs = receiptRows(data.receipts);
  const recWeek = recs.filter((r) => r.date && r.date >= wk.from && r.date <= wk.to);
  const loose = recs.filter((r) => r.loose);
  const receipts = {
    week: { n: recWeek.length, total: Math.round(recWeek.reduce((a, r) => a + r.total, 0) * 100) / 100 },
    noJob: { n: loose.length, total: Math.round(loose.reduce((a, r) => a + r.total, 0) * 100) / 100, rows: loose.slice(0, 5).map((r) => ({ id: r.id, vendor: r.vendor, total: r.total, date: r.date })) },
    days: winDays.map((date) => { const l = recWeek.filter((r) => r.date === date); return { date, n: l.length, total: l.reduce((a, r) => a + r.total, 0) }; }),
    counted: 'receipts the books sent back from the field (BattleBooks), by the receipt’s date', leftOut: ['void and removed receipts'],
  };

  // ── material runs this week (the Store runs goal's count: stretches of 2 min or more) ─────────────────────
  const runGoal = goalValue({ key: 'ops:storeRuns' }, dept, wk, d);
  const runs = [];
  for (const t of tasks) {
    if (t.deletedAt || !isMaterialRun(t)) continue;
    for (const e of t.timeEntries || []) if (e?.date >= wk.from && e.date <= wk.to && (e.duration || 0) >= 120000) runs.push({ taskId: t.id, date: e.date, ms: e.duration, project: projById.get(pidOf(t)) || null });
  }
  runs.sort((a, b) => String(b.date).localeCompare(String(a.date)));
  const storeRuns = {
    n: runGoal.value || 0, ms: runs.reduce((a, r) => a + r.ms, 0), rows: runs, limit: targets['ops:storeRuns'].target,
    days: winDays.map((date) => { const l = runs.filter((r) => r.date === date); return { date, n: l.length, ms: l.reduce((a, r) => a + r.ms, 0) }; }),
    counted: runGoal.basis, leftOut: runGoal.notes || [],
  };

  // ── callbacks this month vs the limit; margin per finished job (last 90 days) vs the target ────────────────
  const month = goalWindow('month', now);
  const cb = goalValue({ key: 'tag:callback' }, dept, month, d);
  const cbMonths = Array.from({ length: 6 }, (_, i) => {
    const at = new Date(now); at.setDate(1); at.setMonth(at.getMonth() - (5 - i));
    const w = goalWindow('month', at.getTime());
    return { from: w.from, n: goalValue({ key: 'tag:callback' }, dept, span(w.from, w.from === month.from ? today : w.to), d).value || 0 };
  });
  const callbacks = { n: cb.value || 0, limit: targets['tag:callback'].target, rows: (cb.rows || []).map((r) => ({ task: byId.get(r.taskId), date: r.date })).filter((r) => r.task), months: cbMonths, counted: 'tasks tagged #callback finished this month (any department)', leftOut: [] };
  const m90 = fieldJobMargin(dept, span(addDaysStr(today, -89), today), d);
  const mMonth = fieldJobMargin(dept, month, d);
  const margins = {
    month: mMonth.value, target: targets.jobMargin.target,
    jobs: (m90.rows || []).map((r) => ({ project: projById.get(r.projectId) || { id: r.projectId, name: 'A job' }, pct: r.pct, paidPct: r.paidPct, revenue: r.revenue, profit: r.profit, date: r.date }))
      .sort((a, b) => String(b.date).localeCompare(String(a.date))),
    counted: m90.basis, leftOut: m90.notes || [],
  };

  // the KPI tiles' extras
  const kpi = {
    worstTask: efficiency.worst[0] || null,
    dayMax: Math.max(0, ...perDay.map((x) => x.ms)),
  };

  return {
    engine: fieldEngine(dept, d, { clock, activeTaskId }),
    range: win.range, window: { from: win.from, to: win.to, days: nDays, label: win.label },
    time: fieldTimeCosts(dept, d, win, days),
    role: deptRole(dept), targets, hours, kpi, earned, efficiency, productive, photos, receipts, runs: storeRuns, callbacks, margins,
  };
}
